import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);
const GUEST_CART_KEY = "riddhi_guest_cart";

function readGuestCart() {
  try {
    const raw = localStorage.getItem(GUEST_CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeGuestCart(items) {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items.map((i) => ({
      product_id: i.product_id, size: i.size, quantity: i.quantity,
    }))));
  } catch {
    // storage quota exceeded — fall through
  }
}

async function hydrate(rawItems) {
  if (!rawItems.length) return [];
  const ids = [...new Set(rawItems.map((i) => i.product_id))];
  const products = {};
  await Promise.all(ids.map(async (id) => {
    try {
      const { data } = await api.get(`/products/${id}`);
      products[id] = data;
    } catch {
      // product may have been removed — leave undefined and the cart row will skip it
    }
  }));
  return rawItems
    .filter((i) => products[i.product_id])
    .map((i) => ({ ...i, product: products[i.product_id] }));
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const mergedForUserId = useRef(null);

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      if (user) {
        // Merge guest cart into server cart the first time this user signs in.
        if (mergedForUserId.current !== user.id) {
          const guest = readGuestCart();
          if (guest.length) {
            for (const g of guest) {
              try {
                await api.post("/cart", g);
              } catch (err) {
                console.error("Merge guest item failed:", err);
              }
            }
            localStorage.removeItem(GUEST_CART_KEY);
          }
          mergedForUserId.current = user.id;
        }
        const { data } = await api.get("/cart");
        setItems(data);
      } else {
        mergedForUserId.current = null;
        setItems(await hydrate(readGuestCart()));
      }
    } catch (err) {
      console.error("Failed to load cart:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = useCallback(async (product_id, size, quantity = 1) => {
    if (user) {
      try {
        const { data } = await api.post("/cart", { product_id, size, quantity });
        setItems(data);
        toast.success("Added to cart");
        return { ok: true };
      } catch (e) {
        console.error("Add to cart failed:", e);
        toast.error("Could not add to cart");
        return { ok: false };
      }
    }
    // Guest path — localStorage
    const raw = readGuestCart();
    const idx = raw.findIndex((i) => i.product_id === product_id && i.size === size);
    if (idx >= 0) raw[idx].quantity += quantity;
    else raw.push({ product_id, size, quantity });
    writeGuestCart(raw);
    setItems(await hydrate(raw));
    toast.success("Added to cart");
    return { ok: true };
  }, [user]);

  const updateItem = useCallback(async (product_id, size, quantity) => {
    if (user) {
      const { data } = await api.put("/cart", { product_id, size, quantity });
      setItems(data);
      return;
    }
    const raw = readGuestCart().map((i) =>
      i.product_id === product_id && i.size === size ? { ...i, quantity } : i
    ).filter((i) => i.quantity > 0);
    writeGuestCart(raw);
    setItems(await hydrate(raw));
  }, [user]);

  const removeItem = useCallback(async (product_id, size) => {
    if (user) {
      const { data } = await api.delete(`/cart/${product_id}/${encodeURIComponent(size)}`);
      setItems(data);
      return;
    }
    const raw = readGuestCart().filter((i) => !(i.product_id === product_id && i.size === size));
    writeGuestCart(raw);
    setItems(await hydrate(raw));
  }, [user]);

  const clear = useCallback(() => {
    if (!user) {
      writeGuestCart([]);
    }
    setItems([]);
  }, [user]);

  const subtotal = items.reduce((s, i) => s + (i.product?.price || 0) * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  const value = useMemo(
    () => ({ items, loading, subtotal, count, addToCart, updateItem, removeItem, fetchCart, clear }),
    [items, loading, subtotal, count, addToCart, updateItem, removeItem, fetchCart, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
